/**
 * Vendor subscriptions.
 *
 * The super admin sets a start and an end date on each vendor from the admin
 * panel (Edit Vendor → Subscription). They are stored on the vendors/{id}
 * document as calendar days, "YYYY-MM-DD":
 *
 *   subscriptionStart  first day the clinic may use the system
 *   subscriptionEnd    LAST day it may — the whole of that day is included
 *
 * The kiosk checks them at login, when the app starts with a remembered
 * session, and continuously while a clinic is signed in, so a kiosk left
 * logged in overnight still signs itself out the moment the plan ends.
 *
 * Kept free of React and Firebase so the rules can be tested on their own.
 * This is the admin panel's JavaScript copy of KinetiQE/src/lib/subscription.ts
 * — change both together.
 */

export const SUBSCRIPTION_WARNING_DAYS = 7;
export const SUPPORT_NAME = "KinetiQ Footcare";
export const SUPPORT_PHONE = "9628960002";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * States: "exempt" (super admin), "unset" (no end date yet — allowed),
 * "notStarted", "active", "expiring" (last SUBSCRIPTION_WARNING_DAYS days),
 * "expired". `blocked` is true for notStarted and expired.
 */

/**
 * A stored day → local midnight at its start.
 *
 * Accepts the "YYYY-MM-DD" the admin panel writes, and tolerates a Date, an
 * ISO string or a Firestore Timestamp in case one is entered by hand in the
 * console. A "YYYY-MM-DD" string is read as a LOCAL day on purpose:
 * `new Date("2026-10-01")` would be UTC midnight, which in India is 05:30 —
 * the plan would end five and a half hours into its last day.
 */
export function parseDay(value) {
  if (value == null || value === "") return null;
  let d = null;
  if (typeof value === "string") {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    d = new Date(value);
  } else if (value instanceof Date) {
    d = value;
  } else if (typeof value === "object" && typeof value.toDate === "function") {
    d = value.toDate();
  } else if (typeof value === "number") {
    d = new Date(value);
  }
  if (!d || Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** The Date as the "YYYY-MM-DD" local day the admin panel stores. */
export function toDayString(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function subscriptionStatus(vendor, now = new Date()) {
  const none = { startsAt: null, endsAt: null, msLeft: null, daysLeft: null };
  if (!vendor) return { state: "unset", blocked: false, ...none };
  if (vendor.isSuperAdmin === true) return { state: "exempt", blocked: false, ...none };

  const startsAt = parseDay(vendor.subscriptionStart);
  const endDay = parseDay(vendor.subscriptionEnd);
  if (!endDay) return { state: "unset", blocked: false, ...none, startsAt };

  const endsAt = new Date(endDay.getFullYear(), endDay.getMonth(), endDay.getDate() + 1);
  const msLeft = endsAt.getTime() - now.getTime();
  const daysLeft = Math.max(0, Math.ceil(msLeft / DAY_MS));
  const base = { startsAt, endsAt, msLeft, daysLeft };

  if (msLeft <= 0) return { state: "expired", blocked: true, ...base };
  if (startsAt && now.getTime() < startsAt.getTime()) return { state: "notStarted", blocked: true, ...base };
  if (msLeft <= SUBSCRIPTION_WARNING_DAYS * DAY_MS) return { state: "expiring", blocked: false, ...base };
  return { state: "active", blocked: false, ...base };
}

/** "6d 04:12:09", or "04:12:09" on the last day. */
export function formatTimeLeft(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const p = (n) => String(n).padStart(2, "0");
  const clock = `${p(Math.floor((total % 86400) / 3600))}:${p(Math.floor((total % 3600) / 60))}:${p(total % 60)}`;
  return days > 0 ? `${days}d ${clock}` : clock;
}

export function formatDay(d) {
  return d ? d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";
}

/** What the blocked-login popup says. */
export function blockedMessage(status) {
  if (status.state === "notStarted") {
    return {
      title: "Your subscription has not started yet",
      body: `Your plan starts on ${formatDay(status.startsAt)}. For access before then, please connect with customer care.`,
    };
  }
  return {
    title: "Your subscription plan is over",
    body: "Please connect with customer care to renew your plan and continue using KinetiQ.",
  };
}

/** The last day the plan covers, as a Date (endsAt is the midnight after it). */
export function lastDay(status) {
  return status.endsAt ? new Date(status.endsAt.getTime() - 1) : null;
}

/** A new end date `months` after the current end (or after today, if it has lapsed). */
export function extendEnd(currentEnd, months, today = new Date()) {
  const end = parseDay(currentEnd);
  const base = end && end >= parseDay(today) ? end : parseDay(today);
  const next = new Date(base.getFullYear(), base.getMonth() + months, base.getDate());
  return toDayString(next);
}
